import React from "react";
import { Link } from "react-router-dom";

interface FeaturedProgramsProps {
  openPopup?: (config?: {
    title?: string;
    subtitle?: string;
    formType?: string;
    interestedProgram?: string;
  }) => void;
}

const programs = [
  {
    title: "10x Data Analyst and AI Complete Certificate Program",
    details: "1to10x Certificate after Completion | 6 months | 25+ Live projects, Case studies & more.",
    description: "Master data analysis and AI with hands-on projects that prepare you for real-world challenges.",
    badge: "FEATURED",
    duration: "6 Months",
    projects: "25+ Live Projects",
    price: "$4,999",
    isHighlighted: true,
    gradient: "from-blue-600 to-cyan-600",
    icon: (
      <svg width="48" height="48" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M3 3V21H21" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
        <path d="M9 9L12 6L16 10L20 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
        <circle cx="9" cy="9" r="2" fill="currentColor"/>
        <circle cx="12" cy="6" r="2" fill="currentColor"/>
        <circle cx="16" cy="10" r="2" fill="currentColor"/>
        <circle cx="20" cy="6" r="2" fill="currentColor"/>
      </svg>
    )
  },
  {
    title: "Become a Generative AI Expert: Unlocking Creativity with Code",
    details: "1to10x Certificate after Completion | 3 months | Access to trending Gen AI tools, & more.",
    description: "Learn to harness the power of generative AI and build innovative solutions that drive business value.",
    badge: "TRENDING",
    duration: "3 Months",
    projects: "AI Tools Access",
    price: "$2,999",
    isHighlighted: false,
    gradient: "from-green-600 to-emerald-600",
    icon: (
      <svg width="48" height="48" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M12 2C13.1046 2 14 2.89543 14 4V6C14 7.10457 13.1046 8 12 8C10.8954 8 10 7.10457 10 6V4C10 2.89543 10.8954 2 12 2Z" fill="currentColor"/>
        <path d="M12 16C13.1046 16 14 16.8954 14 18V20C14 21.1046 13.1046 22 12 22C10.8954 22 10 21.1046 10 20V18C10 16.8954 10.8954 16 12 16Z" fill="currentColor"/>
        <path d="M4 10C2.89543 10 2 10.8954 2 12C2 13.1046 2.89543 14 4 14H6C7.10457 14 8 13.1046 8 12C8 10.8954 7.10457 10 6 10H4Z" fill="currentColor"/>
        <path d="M18 10C16.8954 10 16 10.8954 16 12C16 13.1046 16.8954 14 18 14H20C21.1046 14 22 13.1046 22 12C22 10.8954 21.1046 10 20 10H18Z" fill="currentColor"/>
        <circle cx="12" cy="12" r="3" stroke="currentColor" strokeWidth="2" fill="none"/>
      </svg>
    )
  },
  {
    title: "10x Agentic AI and Automation Mastery",
    details: "1to10x Certificate after Completion | 4 months | Advanced AI agents, automation workflows & more.",
    description: "Master the development of autonomous AI agents and orchestrate complex workflows that operate independently to drive business value.",
    badge: "MOST POPULAR",
    duration: "4 Months",
    projects: "Advanced AI Projects",
    price: "$4,999",
    isHighlighted: true,
    gradient: "from-purple-600 to-violet-600",
    icon: (
      <svg width="48" height="48" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M12 2L2 7L12 12L22 7L12 2Z" fill="currentColor"/>
        <path d="M2 17L12 22L22 17" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
        <path d="M2 12L12 17L22 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
        <circle cx="12" cy="7" r="1.5" fill="#1F2937"/>
        <circle cx="7" cy="9.5" r="1" fill="#1F2937"/>
        <circle cx="17" cy="9.5" r="1" fill="#1F2937"/>
      </svg>
    )
  },
];

const FeaturedPrograms: React.FC<FeaturedProgramsProps> = ({ openPopup }) => (
  <section id="featured-programs" className="py-16 sm:py-20 px-4 relative overflow-hidden -mt-64 sm:-mt-16 z-20" style={{ backgroundColor: '#111827' }}>
    {/* Background Elements */}
    <div className="absolute inset-0 opacity-5">
      <div className="absolute top-10 right-10 w-40 h-40 border border-blue-500 rounded-full"></div>
      <div className="absolute bottom-10 left-10 w-32 h-32 border border-purple-500 rounded-full"></div>
      <div className="absolute top-1/2 right-1/4 w-20 h-20 border border-green-500 rounded-full"></div>
    </div>

    <div className="max-w-7xl mx-auto relative z-10">
      {/* Header */}
      <div className="text-center mb-16">
        <div className="inline-block bg-gradient-to-r from-blue-600/20 to-purple-600/20 text-blue-400 px-6 py-3 rounded-full text-sm font-semibold mb-6 border border-blue-500/30">
          🚀 MOST POPULAR COURSES
        </div>
        <h2 className="text-4xl md:text-5xl font-bold text-white mb-6 leading-tight">
          Featured <span className="bg-gradient-to-r from-blue-400 to-purple-400 bg-clip-text text-transparent">Programs</span>
        </h2>
        <p className="text-xl text-gray-300 max-w-4xl mx-auto leading-relaxed">
          Transform your career with our <span className="text-blue-400 font-semibold">1to10x certificate programs</span> designed by experts for real-world practice
        </p>
      </div>

      {/* Cards Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
        {programs.map((program, idx) => (
          <div 
            key={idx} 
            className={`group rounded-xl border border-gray-700/50 shadow-2xl hover:shadow-blue-500/10 transition-all duration-500 hover:border-blue-500/50 hover:-translate-y-2 overflow-hidden relative ${
              program.isHighlighted ? 'md:col-span-2 lg:col-span-1 lg:transform lg:scale-105' : ''
            }`}
            style={{ backgroundColor: '#1A1F25' }}
          >
            {/* Background Gradient on Hover */}
            <div className="absolute inset-0 bg-gradient-to-br from-blue-500/5 via-transparent to-purple-500/5 opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
            
            {/* Custom Gradient Header with Icon */}
            <div className="relative h-40 overflow-hidden">
              <div className={`w-full h-full bg-gradient-to-br ${program.gradient} flex items-center justify-center relative`}>
                {/* Geometric Pattern Background */}
                <div className="absolute inset-0 opacity-10">
                  <div className="absolute top-4 left-4 w-8 h-8 border border-white rounded-full"></div>
                  <div className="absolute top-8 right-8 w-4 h-4 border border-white rounded-full"></div>
                  <div className="absolute bottom-6 left-8 w-6 h-6 border border-white rounded-full"></div>
                  <div className="absolute bottom-4 right-4 w-3 h-3 bg-white rounded-full"></div>
                  
                  {/* Grid pattern */}
                  <div className="absolute inset-0" style={{
                    backgroundImage: 'linear-gradient(rgba(255,255,255,0.1) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.1) 1px, transparent 1px)',
                    backgroundSize: '20px 20px'
                  }}></div>
                </div>

                {/* Main Icon */}
                <div className="text-white z-10 transform group-hover:scale-110 transition-transform duration-300">
                  {program.icon}
                </div>

                {/* Course Type Label */}
                <div className="absolute bottom-4 left-4 bg-black/30 backdrop-blur-sm rounded-lg px-3 py-1">
                  <span className="text-white text-sm font-semibold">
                    {idx === 0 ? 'DATA ANALYTICS' : idx === 1 ? 'GENERATIVE AI' : 'AGENTIC AI'}
                  </span>
                </div>
              </div>

              {/* Badge */}
              <div className="absolute top-3 right-3">
                <span className={`px-2 py-1 rounded-full text-xs font-bold shadow-lg backdrop-blur-sm ${
                  program.badge === 'MOST POPULAR' 
                    ? 'bg-red-500/90 text-white' 
                    : program.badge === 'TRENDING'
                    ? 'bg-green-500/90 text-white'
                    : 'bg-white/90 text-blue-600'
                }`}>
                  {program.badge}
                </span>
              </div>

              {/* Price */}
              <div className="absolute top-3 left-3 bg-black/60 backdrop-blur-sm rounded-lg px-2 py-1 border border-white/20">
                <span className="text-sm font-bold text-white">{program.price}</span>
              </div>

              {/* Highlight indicator */}
              {program.isHighlighted && (
                <div className="absolute bottom-3 right-3 bg-yellow-400/90 text-yellow-900 px-2 py-1 rounded-full text-xs font-bold backdrop-blur-sm">
                  ⭐ Popular
                </div>
              )}
            </div>

            {/* Content */}
            <div className="relative z-10 p-4 sm:p-5">
              <h3 className="text-base sm:text-lg font-bold text-white mb-3 line-clamp-2 group-hover:text-blue-400 transition-colors leading-tight">
                {program.title}
              </h3>
              
              <p className="text-gray-400 mb-4 line-clamp-2 text-xs sm:text-sm leading-relaxed">
                {program.description}
              </p>

              {/* Course Stats */}
              <div className="flex items-center gap-3 sm:gap-4 mb-4 sm:mb-5 text-xs">
                <div className="flex items-center gap-1 text-gray-300">
                  <svg className="w-3 h-3 text-blue-400 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z" clipRule="evenodd" />
                  </svg>
                  <span className="truncate">{program.duration}</span>
                </div>
                <div className="flex items-center gap-1 text-gray-300">
                  <svg className="w-3 h-3 text-green-400 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                    <path d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <span className="truncate">{program.projects}</span>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex flex-col sm:flex-row gap-2">
                <Link 
                  to={
                    idx === 0 ? "/data-analyst-course" : 
                    idx === 1 ? "/generative-ai-course" : 
                    "/agentic-ai-course"
                  }
                  className="w-full sm:flex-1 bg-blue-600 hover:bg-blue-700 text-white px-3 py-2.5 rounded-lg text-xs sm:text-xs font-semibold transition-all duration-200 hover:scale-[1.02] text-center shadow-lg inline-block"
                >
                  View Details
                </Link>
                <button 
                  onClick={() => openPopup?.({
                    title: "Apply Now",
                    subtitle: `Apply for ${program.title} and start your transformation journey`,
                    formType: "course_inquiry",
                    interestedProgram: program.title
                  })}
                  className="w-full sm:flex-1 border border-gray-600 text-gray-300 hover:bg-gray-700 hover:text-white hover:border-gray-500 px-3 py-2.5 rounded-lg text-xs sm:text-xs font-medium transition-all duration-200 text-center"
                >
                  Apply Now
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>

    </div>
  </section>
);

export default FeaturedPrograms; 
